<template>
    <TprmModal
        open
        title="View Note"
        subtitle="Note details and information."
        size="md"
        allow-close
        @close="$emit('close')"
    >
        <div class="NoteModal">
            <div v-if="loading" class="NoteModal__loading">
                Loading...
            </div>
            <template v-else-if="noteText">
                <div class="NoteModal__section">
                    <div class="NoteModal__sectionLabel">
                        Note Content
                    </div>
                    <div class="NoteModal__noteText">
                        {{ noteText }}
                    </div>
                </div>
            </template>
            <slot name="after-note-content" />
            <div class="NoteModal__section">
                <div class="NoteModal__sectionLabel">
                    Added By
                </div>
                <div class="NoteModal__value">
                    {{ addedBy }}
                </div>
            </div>
            <div class="NoteModal__section">
                <div class="NoteModal__sectionLabel">
                    Date
                </div>
                <div class="NoteModal__value">
                    {{ date }}
                </div>
            </div>
            <div v-if="error" class="NoteModal__errorBanner">
                {{ error }}
            </div>
        </div>
        <template #footer>
            <slot name="footer">
                <NewButton
                    variant="outline"
                    color="neutral"
                    @click="$emit('close')"
                >
                    Close
                </NewButton>
            </slot>
        </template>
    </TprmModal>
</template>

<style lang="scss">
.NoteModal {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__loading {
        color: var(--eidTechGray);
        font-size: var(--text-small-size, 13px);
    }

    &__section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__sectionLabel {
        font-size: var(--text-label-size, 12px);
        font-weight: var(--text-label-weight, 600);
        color: var(--eidSecureBlue-50);
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &__noteText {
        white-space: pre-wrap;
        font-size: var(--text-body-size, 14px);
        line-height: 1.6;
        color: var(--eidSecureBlue);
        padding: 12px;
        background: var(--eidBgGrayTones-lightest);
        border: 1px solid var(--eidSecureBlue-25);
        border-radius: 6px;
        min-height: 100px;
    }

    &__value {
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue);
    }

    &__errorBanner {
        padding: 10px 12px;
        background-color: var(--eidDangerRed-10);
        border: 1px solid var(--eidDangerRed);
        border-radius: 6px;
        color: var(--eidDangerRed);
        font-size: var(--text-small-size, 13px);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    // PRODUCT-26110 QA fixes4: declares the `close` event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'close' but it is
    // neither declared in the emits option nor as an 'onClose' prop" every time it fires.
    export default defineComponent({
        name: 'NoteModal',
        components: { TprmModal, NewButton },
        props: {
            loading: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            error: {
                type: String as PropType<string>,
                default: '',
            },
            noteText: {
                type: String as PropType<string | null>,
                default: null,
            },
            addedBy: {
                type: String as PropType<string>,
                default: '',
            },
            date: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'close' ],
    });
</script>
